// ANCHOR React
import { useCallback, useState } from 'react';

// ANCHOR Base
import { StatefulMenu } from 'baseui/menu';

// ANCHOR Graph State
import { useAuthToken } from '@lyon/nodes/firebase/auth/auth-token-node';

// ANCHOR Hooks
import { usePromise } from '@lyon/utils/hooks/usePromise';
import { useSentryExceptionHandler } from '@lyon/utils/hooks/useSentryExceptionHandler';
import { useConstantCallback } from '@lyon/utils/hooks/useConstantCallback';

// ANCHOR Models
import { ConfirmationModalVisibility } from '@lyon/scoped-models/confirmation-modal/ConfirmationModalVisibility';
import { useFormQuestionsMutate } from '@lyon/scoped-models/form/FormQuestionsData';
import { useUpdateFormQuestionPositionModalVisibilityShow } from '@lyon/scoped-models/form/update/UpdateFormQuestionPositionModalVisibility';
import { useFormQuestionItemDataID } from '@lyon/scoped-models/form/FormQuestionItemData';

// ANCHOR API
import { deleteFormQuestionById } from '@lyon/utils/api/forms';

// ANCHOR Components
import { constant } from '@lyon/components/utils/constant';
import { LyonConfirmationModal } from '@lyon/components/confirmation-modal/LyonConfirmationModal';

// ANCHOR Constants
import { LyonUpdateFormPosition } from '@lyon/components/forms/LyonUpdateFormPosition';
import {
  TYPE,
  PROMPT,
  MENU_ITEMS,
  PROMPT_DESCRIPTION,
} from './constants';

interface IItem {
  label: string;
}

interface IITemSelect {
  item: IItem;
}

export const LyonUpdateFormOutlineTabListItemMenu = constant(() => {
  const catchException = useSentryExceptionHandler();
  const token = useAuthToken();
  const questionId = useFormQuestionItemDataID();
  const mutate = useFormQuestionsMutate();

  const showDeleteModal = ConfirmationModalVisibility.useSelector((state) => state.show);
  const showUpdateModal = useUpdateFormQuestionPositionModalVisibilityShow();

  const [loading, setLoading] = useState(false);

  const mounted = usePromise([token, questionId]);

  const onMenuItemSelect = useConstantCallback(({ item }: IITemSelect) => {
    switch (item.label) {
      case 'Change Question Number': {
        showUpdateModal();
        break;
      }
      case 'Delete Question': {
        showDeleteModal();
        break;
      }
      default:
        break;
    }
  });

  const onConfirmModalClick = useCallback(async () => {
    if (token && questionId) {
      setLoading(true);

      try {
        await mounted(deleteFormQuestionById({
          token,
          id: questionId,
        }));

        await mounted(mutate());
      } catch (err) {
        catchException(err);
      }

      setLoading(false);
    }
  }, [token, questionId, mounted, mutate, catchException]);

  return (
    <>
      <StatefulMenu
        items={MENU_ITEMS}
        onItemSelect={onMenuItemSelect}
      />
      <LyonUpdateFormPosition />
      <LyonConfirmationModal
        contentType={TYPE}
        prompt={PROMPT}
        description={PROMPT_DESCRIPTION}
        asyncCallback={onConfirmModalClick}
        loading={loading}
        isStrict={false}
      />
    </>
  );
});
